import { useEffect, useState, useRef } from 'react';
import { UploadCloud, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

import { logError } from '@/utils/logger.utils';
import { ApiTypes } from '@/types/server/api.types';
import posthog from 'posthog-js';
import { handleLogout } from '@/utils/auth.utils';

type CVUploadSectionProps = {
	onNext: () => void;
	setIsUserLoggedIn: (isUserLoggedIn: boolean) => void;
	setUserDetails: (
		userDetails: ApiTypes.TGetCandidateDetailsResponse['data'] | null
	) => void;
};

export const CVUploadSection = ({
	onNext,
	setIsUserLoggedIn,
	setUserDetails,
}: CVUploadSectionProps) => {
	const [isLoading, setIsLoading] = useState(true);
	const [isUploading, setIsUploading] = useState(false);
	const [candidateCVStatus, setCandidateCVStatus] = useState<
		'noCVsUploaded' | 'allCVsCancelled' | null
	>(null);

	// Use separate refs for the different states
	const newUploadFileInputRef = useRef<HTMLInputElement>(null);
	const failedUploadFileInputRef = useRef<HTMLInputElement>(null);

	const getCandidateCVStatus = async () => {
		try {
			const token = window.localStorage.getItem('token');

			const response = await fetch(
				`${import.meta.env.VITE_SERVER_API_PREFIX}/candidate-cvs/status`,
				{
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
				}
			);

			const data =
				(await response.json()) as ApiTypes.TGetCandidateCVStatusResponse;

			if (data.httpResponseCode !== 200) {
				toast('CV Status Error', {
					description: data.message,
					id: 'cv-status-error',
				});

				if (data.httpResponseCode === 401) {
					handleLogout();
					setIsUserLoggedIn(false);
					setUserDetails(null);
				}

				return;
			}

			if (data.identificationCode === 'getCandidateCVStatus_noCVsUploaded') {
				setCandidateCVStatus('noCVsUploaded');
			} else if (
				data.identificationCode ===
				'getCandidateCVStatus_cvProcessingInProgress'
			) {
				onNext();
				return;
			} else if (
				data.identificationCode === 'getCandidateCVStatus_cvProcessingCompleted'
			) {
				onNext();
				return;
			} else if (
				data.identificationCode === 'getCandidateCVStatus_allCVsCancelled'
			) {
				setCandidateCVStatus('allCVsCancelled');
			}

			setIsLoading(false);
		} catch (error) {
			logError(error);

			toast('CV Status Error', {
				description:
					'Failed to get CV status. Please refresh the page and try again.',
				id: 'cv-status-error',
			});
		}
	};

	// Reset function to ensure clean state for new upload
	const resetFileInputs = () => {
		if (newUploadFileInputRef.current) {
			newUploadFileInputRef.current.value = '';
		}
		if (failedUploadFileInputRef.current) {
			failedUploadFileInputRef.current.value = '';
		}
	};

	const handleNewUploadClick = () => {
		posthog.capture('CVUploadSection_UploadNewCVClicked');

		// Prevent upload picker when processing is happening
		if (isUploading) return;

		resetFileInputs();
		if (newUploadFileInputRef.current) {
			newUploadFileInputRef.current.click();
		}
	};

	const handleFailedUploadClick = () => {
		posthog.capture('CVUploadSection_UploadCVAgainClicked');

		// Prevent upload picker when processing is happening
		if (isUploading) return;

		resetFileInputs();
		if (failedUploadFileInputRef.current) {
			failedUploadFileInputRef.current.click();
		}
	};

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) {
			return;
		}

		// Check file type
		if (file.type !== 'application/pdf') {
			toast('Invalid Format', {
				description: 'Only PDF files are accepted.',
				id: 'invalid-file-format',
			});
			return;
		}

		// Check file size (5MB)
		if (file.size > 5 * 1024 * 1024) {
			toast('File Too Large', {
				description: 'File size must be less than 5MB.',
				id: 'file-too-large',
			});
			return;
		}

		await uploadCV(file);
	};

	const uploadCV = async (file: File) => {
		try {
			setIsUploading(true);

			// Reset the file inputs for future uploads
			resetFileInputs();

			const token = window.localStorage.getItem('token');

			// Step 1: Initiate upload to get presigned URL
			const initiateResponse = await fetch(
				`${
					import.meta.env.VITE_SERVER_API_PREFIX
				}/candidate-cvs/initiate-upload`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						fileSizeInBytes: file.size,
						fileMimeType: file.type,
						fileExtension: file.name.split('.').pop(),
					}),
				}
			);

			const initiateData =
				(await initiateResponse.json()) as ApiTypes.TInitiateCVUploadResponse;

			if (initiateData.httpResponseCode !== 200 || !initiateData.data) {
				toast('Upload Error', {
					description: initiateData.message || 'Failed to initiate upload.',
					id: 'initiate-upload-error',
				});

				if (initiateData.httpResponseCode === 401) {
					handleLogout();
					setIsUserLoggedIn(false);
					setUserDetails(null);
				}

				setIsUploading(false);
				return;
			}

			const { presignedUrl, candidateCVId } = initiateData.data;

			// Step 2: Upload file to presigned URL
			const uploadResponse = await fetch(presignedUrl, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': 'application/pdf',
				},
			});

			if (!uploadResponse.ok) {
				throw new Error(uploadResponse.statusText);
			}

			// Step 3: Complete upload process
			const completeResponse = await fetch(
				`${
					import.meta.env.VITE_SERVER_API_PREFIX
				}/candidate-cvs/complete-upload`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({ candidateCVId }),
				}
			);

			const completeData =
				(await completeResponse.json()) as ApiTypes.TCompleteCVUploadResponse;

			if (completeData.httpResponseCode !== 200) {
				toast('Error while uploading CV', {
					description:
						completeData.message ||
						'Failed to complete upload process. Please refresh the page and try again.',
					id: 'complete-upload-error',
				});

				if (completeData.httpResponseCode === 401) {
					handleLogout();
					setIsUserLoggedIn(false);
					setUserDetails(null);

					return;
				}

				setCandidateCVStatus('allCVsCancelled');

				setIsUploading(false);
				return;
			}

			toast('Success', {
				description: 'Your CV has been uploaded successfully.',
				id: 'upload-success',
			});
			setIsUploading(false);

			onNext();
			return;
		} catch (error) {
			logError(error);
			toast('Upload Error', {
				description: 'Failed to upload CV.',
				id: 'upload-cv-error',
			});
			setIsUploading(false);
		}
	};

	useEffect(() => {
		getCandidateCVStatus();
	}, []);

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-64 bg-white rounded-md shadow-md">
				<Loader2 className="h-10 w-10 animate-spin text-blue-600" />
			</div>
		);
	}

	return (
		<div className="bg-white rounded-md shadow-lg p-6 space-y-6 border border-gray-100 transition-all">
			<div>
				<h2 className="text-xl font-semibold text-gray-800">Upload your CV</h2>
				<p className="text-sm text-gray-500 mt-3">
					Upload your CV so we can match you to 100s of job openings
				</p>
			</div>

			<Separator />

			{/* Different content based on CV status */}
			{candidateCVStatus === 'noCVsUploaded' && (
				<>
					<input
						type="file"
						ref={newUploadFileInputRef}
						onChange={handleFileChange}
						accept="application/pdf"
						className="hidden"
						key="upload-new-cv"
					/>
					<div
						onClick={handleNewUploadClick}
						className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center text-center space-y-2 hover:bg-gray-50 transition-colors duration-200 cursor-pointer"
					>
						{isUploading ? (
							<>
								<Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
								<p className="text-sm font-medium text-gray-700">
									Uploading CV...
								</p>
							</>
						) : (
							<>
								<UploadCloud className="h-8 w-8 text-gray-400" />
								<p className="text-sm font-medium text-gray-700">Upload CV</p>
								<p className="text-xs text-gray-400">
									Only PDFs under 5MB accepted
								</p>
							</>
						)}
					</div>
				</>
			)}

			{candidateCVStatus === 'allCVsCancelled' && (
				<>
					<input
						type="file"
						ref={failedUploadFileInputRef}
						onChange={handleFileChange}
						accept="application/pdf"
						className="hidden"
						key="upload-failed-cv"
					/>
					<div className="rounded-xl p-6 flex flex-col items-center justify-center text-center space-y-4 bg-red-50 border border-red-100">
						<AlertCircle className="h-8 w-8 text-red-500" />
						<p className="text-sm font-medium text-red-700">
							CV Processing Failed
						</p>
						<p className="text-xs text-red-600 max-w-md">
							We couldn't process your CV. This could be due to formatting
							issues or file corruption. Please make sure the file is a valid
							PDF file with appropriate CV content.
						</p>
					</div>
					<div
						onClick={() => {
							handleFailedUploadClick();
						}}
						className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center text-center space-y-2 hover:bg-gray-50 transition-colors duration-200 cursor-pointer mt-4"
					>
						{isUploading ? (
							<>
								<Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
								<p className="text-sm font-medium text-gray-700">
									Uploading CV...
								</p>
							</>
						) : (
							<>
								<UploadCloud className="h-8 w-8 text-gray-400" />
								<p className="text-sm font-medium text-gray-700">
									Upload CV Again
								</p>
								<p className="text-xs text-gray-400">
									Only PDFs under 5MB accepted
								</p>
							</>
						)}
					</div>
				</>
			)}

			<div className="flex justify-end pt-4">
				<Button
					onClick={onNext}
					className="bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
					disabled={
						candidateCVStatus === 'noCVsUploaded' ||
						candidateCVStatus === 'allCVsCancelled' ||
						isUploading
					}
				>
					Next
				</Button>
			</div>
		</div>
	);
};
